/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LoadingPanelService } from '../shared/loading-panel/loading-panel.service';
import { GenericModalService } from '../shared/generic-modal/generic-modal.service';
import { ToastService, ToastType } from '../shared/toast/toast.service';
import { TransportService } from '../transport/services/transport.service';
import { PartnerSystemService } from '../partner/services/partner-system.service';
import { TransportOutConfiguration } from '../transport/models/transport-configuration';
import { PartnerComponentBase } from './partner-search-details.base';
import { CurrentUserService } from '../user/services/current-user.service';
import { BizDataGridDirective } from '../shared/grid/biz-grid.directive';
import { TransportType } from '../transport/models/transport-type';
import { PartnerSystem } from './models';
import { DxDataGridModule } from 'devextreme-angular';
import { DxoEditingModule, DxiColumnModule, DxoLookupModule } from 'devextreme-angular/ui/nested';
import { DxTemplateModule } from 'devextreme-angular/core';
import { StatusTrafficLightComponent } from '../shared/status-traffic-light/status-traffic-light.component';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';
import { TransportConfigurationEditComponent } from '../transport/transport-configuration-edit.component/transport-configuration-edit.component';
import { BizGridFiltervalues } from '../shared/grid/biz-grid-filtervalues';
import { BizUserColumnDirective } from '../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../shared/grid/biz-grid-date.directive';

@Component({
  templateUrl: './partner-search-details-locations.component.html',
  selector: 'ep-partner-details-locations',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxiColumnModule, DxoLookupModule, DxTemplateModule, StatusTrafficLightComponent, PermissionRequiredDirective, TransportConfigurationEditComponent, BizUserColumnDirective, BizDateColumnDirective]
})
export class PartnerSearchDetailsLocationsComponent extends PartnerComponentBase implements OnInit {
  public bizGridFiltervalues = inject(BizGridFiltervalues);
  private route = inject(ActivatedRoute);
  private transportService = inject(TransportService);
  private partnerSystemService = inject(PartnerSystemService);
  private toastService = inject(ToastService);
  private modalService = inject(GenericModalService);
  private loadingPanelService = inject(LoadingPanelService);
  private currentUserService = inject(CurrentUserService);

  private hasPermissionToEdit = false;
  private hasPermissionToDelete = false;
  private hasPermissionToCopy = false;

  public transportConfigurations: TransportOutConfiguration[] = [];
  public currentTransportConfiguration: TransportOutConfiguration;
  public transportTypes: TransportType[] = [];
  public partnerSystems: PartnerSystem[] = [];
  public directEditTransportConfigurationId: number; // Used if it's a direct deep-link into 'edit' transport-configuration.
  public isLoading = false;
  public someBlockedByImport = false;

  ngOnInit(): void {
    this.hasPermissionToEdit = this.currentUserService.hasPermission('Transport_Action_OutgoingLocation_Edit');
    this.hasPermissionToDelete = this.currentUserService.hasPermission('Transport_Action_OutgoingLocation_Delete');
    this.hasPermissionToCopy = this.currentUserService.hasPermission('Transport_Action_OutgoingLocation_Copy');

    this.route.params.subscribe((parms: any) => {
      if (parms.transportConfigurationId) {
        this.directEditTransportConfigurationId = +parms.transportConfigurationId;
      }
    });
  }

  async partnerSet(): Promise<void> {
    await this.loadTransportConfigurations();
  }

  async loadTransportConfigurations(): Promise<void> {
    const partnerId = this.selectedPartner?.PartnersId;

    this.transportTypes = await this.transportService.getTransportTypes(this.transportService.transportTypesOut);
    this.partnerSystems = await this.partnerSystemService.getAllPartnerSystems();

    //  Load all transport-configurations
    const ptocs = partnerId
      ? (await this.transportService.getPartnerTransportOutConfigurations(this.selectedPartner.PartnersId, false)) ?? []
      : [];
    this.transportConfigurations = ptocs.filter(t => !!t.PartnerId);
    let directEditTransportConfiguration: TransportOutConfiguration;

    // Check if it's a deep-link and select the transport-configuration to edit.
    if (this.directEditTransportConfigurationId) {
      directEditTransportConfiguration = ptocs.find(t => t.Id === this.directEditTransportConfigurationId);
    }

    this.someBlockedByImport = ptocs.some(t => t.BlockedByImport);

    this.transportConfigurations.forEach(config => {
      (config as any).canEdit = this.hasPermissionToEdit;
      (config as any).canDelete = this.hasPermissionToDelete;
      (config as any).canClone = this.hasPermissionToCopy;
    });

    if (directEditTransportConfiguration) {
      this.editTransportConfiguration(directEditTransportConfiguration);
    }
  }

  getTransportTypeName = (rowData: TransportOutConfiguration) => {
    const result = this.transportTypes.find(t => t.Id === rowData.TransportTypeId)?.Name;
    return result;
  };

  newLocation() {
    let hasInternalPartnerPermission = this.currentUserService.hasPermission('Partner_Action_InternalPartner');
    if (this.selectedPartner && this.selectedPartner.PartnerType && this.selectedPartner.PartnerType.Name !== 'Internal') {
      // For non-internal partners, ignore the 'internal' permission by always granting it.
      hasInternalPartnerPermission = true;
    }

    if (hasInternalPartnerPermission) {
      super.setEditMode(true);
      this.currentTransportConfiguration = {} as TransportOutConfiguration;
      (this.currentTransportConfiguration as any).isNew = true;
      this.currentTransportConfiguration.PartnerId = this.selectedPartner.PartnersId;
    }
    else {
      this.toastService.showToast('Operation is not allowed on internal partners', ToastType.error);
    }
  }

  editTransportConfiguration(transportConfiguration: TransportOutConfiguration) {
    if (this.hasPermissionToEdit) {
      this.currentTransportConfiguration = transportConfiguration;
      super.setEditMode(true);
    }
  }

  cloneTransportConfiguration(transportConfiguration: TransportOutConfiguration) {
    this.modalService.confirmDirect('Copy ' + transportConfiguration.Name, 'Do you want to make a copy of ' + transportConfiguration.Name + '?').then(async () => {
      this.loadingPanelService.showLoadingPanelDirect('Copying ' + transportConfiguration.Name + '...');
      const clonedTransportConfiguration = await this.transportService.cloneTransportOutConfiguration(transportConfiguration);
      await this.partnerSet();
      this.loadingPanelService.hideLoadingPanel();
      this.currentTransportConfiguration = clonedTransportConfiguration;
      super.setEditMode(true);
    }).catch(() => { });
  }

  async saved(): Promise<void> {
    await this.loadTransportConfigurations();
    this.toastService.showToast('Location saved...', ToastType.success);
    this.cancel(); // Let cancel do the rest...
  }

  cancel() {
    this.currentTransportConfiguration = null;
    super.setEditMode(false);
  }

  async deleteTransportConfiguration(transportConfiguration: TransportOutConfiguration): Promise<void> {
    try {
      await this.modalService.confirmDirect('Delete', `Delete ${transportConfiguration.Name}?`);
      this.loadingPanelService.showLoadingPanelDirect('Deleting...');
    }
    catch {
      return;
      // User cancelled
    }

    await this.transportService.deleteTransportOutConfiguration(transportConfiguration.Id)
    const index = this.transportConfigurations.indexOf(transportConfiguration);
    this.transportConfigurations.splice(index, 1);
    this.loadingPanelService.hideLoadingPanel();
    this.toastService.showToast('Location deleted...', ToastType.success);
    await this.loadTransportConfigurations();
  }

  onToolbarPreparing(e) {
    if (this.currentUserService.hasPermission('Transport_Action_OutgoingLocation_Create')) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      e.toolbarOptions.items.unshift({
        location: 'after',
        name: 'addNewTransportConfiguration',
        widget: 'dxButton',
        options: {
          icon: 'add',
          onClick: () => this.newLocation()
        }
      });
    }
  }

  async unblockLocation(location: TransportOutConfiguration): Promise<void> {
    try {
      await this.modalService.confirmDirect(`Unblock ${location.Name}`, 'Do you want to unblock this location?');
    } catch {
      return;
      // User cancelled
    }

    try {
      await this.transportService.unblockTransportOutConfigurations([location.Id]);
      this.toastService.showToast('Location unblocked successfully.', ToastType.success);
    } catch {
      this.toastService.showToast('Failed to unblock location.', ToastType.error);
    } finally {
      await this.loadTransportConfigurations();
    }
  }
}
